/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
/* eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports --
   preexisting violation https://github.com/github/react-platform/issues/1135 */
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {GlobalCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {type Environment, fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {Subscription} from 'relay-runtime'
import type {ProjectPickerRelay$key} from './__generated__/ProjectPickerRelay.graphql'

import {IDS} from '@github-ui/item-picker-base/constants/ids'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import type {
  ProjectPickerRelayQuery,
  ProjectPickerRelayQuery$data,
} from './__generated__/ProjectPickerRelayQuery.graphql'
import {useDebounce} from '@github-ui/use-debounce'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {SELECTORS} from '@github-ui/item-picker-base/constants/selectors'
import {useKeyPress} from '@github-ui/use-key-press'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TableIcon} from '@primer/octicons-react'
import {
  ProjectPickerBase,
  type ProjectPickerBaseProps,
  type ProjectPickerBaseProject,
} from '@github-ui/item-picker-project'
import {ProjectPicker as ProjectPickerTsq} from '@github-ui/item-picker-project-tsq'

const recentGroup: ItemGroup = {groupId: 'recent', header: {title: 'Recent', variant: 'filled'}}
const repoGroup: ItemGroup = {groupId: 'repository', header: {title: 'Repository', variant: 'filled'}}
const orgGroup: ItemGroup = {groupId: 'organization', header: {title: 'Organization', variant: 'filled'}}
const selectedGroup: ItemGroup = {groupId: 'selected'}

export type ProjectPickerRelayProject = ProjectPickerBaseProject

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ProjectPickerRelayFragment = graphql`
  fragment ProjectPickerRelay on ProjectV2 @inline {
    id
    title
    closed
    number
    url
    viewerCanUpdate
    hasReachedItemsLimit
    __typename
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const ProjectPickerGraphqlQuery = graphql`
  query ProjectPickerRelayQuery($owner: String!, $repo: String!, $query: String) {
    repository(owner: $owner, name: $repo) {
      projectsV2(first: 5, query: $query, orderBy: {field: RELEVANCE, direction: DESC}, useFullTermQuery: true) {
        nodes {
          ...ProjectPickerRelay
        }
      }
      recentProjects(first: 5) {
        edges {
          node {
            ...ProjectPickerRelay
          }
        }
      }
      owner {
        ... on Organization {
          projectsV2(first: 5, orderBy: {field: RELEVANCE, direction: DESC}, query: $query, useFullTermQuery: true) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
        }
        ... on User {
          projectsV2(first: 5, orderBy: {field: RELEVANCE, direction: DESC}, query: $query, useFullTermQuery: true) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
          recentProjects(first: 5) {
            edges {
              node {
                ...ProjectPickerRelay
              }
            }
          }
        }
      }
    }
  }
`

export async function fetchProjects(
  environment: Environment,
  options: {
    owner: string
    repo: string
    query: string
  },
): Promise<{
  repositoryProjects: ProjectPickerRelayProject[]
  recentRepositoryProjects: ProjectPickerRelayProject[]
  ownerProjects: ProjectPickerRelayProject[]
  recentOwnerProjects: ProjectPickerRelayProject[]
}> {
  const {owner, repo, query} = options

  const data = await fetchQuery<ProjectPickerRelayQuery>(
    environment,
    ProjectPickerGraphqlQuery,
    {
      owner,
      repo,
      query,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  const repositoryProjects = (data?.repository?.projectsV2?.nodes ?? [])
    .filter(node => !!node)
    // eslint-disable-next-line no-restricted-syntax
    .map(node => readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, node))

  const recentRepositoryProjects = (data?.repository?.recentProjects?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    // eslint-disable-next-line no-restricted-syntax
    .map(node => readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, node))

  const ownerProjects = (data?.repository?.owner?.projectsV2?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    // eslint-disable-next-line no-restricted-syntax
    .map(node => readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, node))

  const recentOwnerProjects = (data?.repository?.owner?.recentProjects?.edges ?? [])
    .map(edge => edge?.node)
    .filter(node => !!node)
    // eslint-disable-next-line no-restricted-syntax
    .map(node => readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, node))

  return {repositoryProjects, recentRepositoryProjects, ownerProjects, recentOwnerProjects}
}

export type ProjectPickerTestQueries = {
  ProjectPickerRelayQuery: ProjectPickerRelayQuery
}

export type ProjectPickerProps = Omit<ProjectPickerBaseProps, 'onSelectionChange'> & {
  pickerId: string
  repo: string
  owner: string
  selectedProjects: ProjectPickerRelayProject[]
  onSave: (projects: ProjectPickerRelayProject[]) => void
  getSelectedProjects?: (projects: ProjectPickerRelayProject[]) => ProjectPickerRelayProject[]
}

type SelectPanelWrapperProps = ProjectPickerProps & {
  projectsData: ProjectPickerRelayQuery$data | null
}

export function ProjectPicker(props: ProjectPickerProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_project_tsq_migration')

  if (isTsqPickerEnabled) {
    return <ProjectPickerTsq {...props} />
  }

  return <ProjectPickerRelay {...props} />
}

function ProjectPickerRelay({anchorElement, triggerOpen, ...rest}: ProjectPickerProps) {
  const [wasTriggered, setWasTriggered] = useState(triggerOpen)
  const handleGlobalCommand = useCallback(() => {
    if (!wasTriggered) {
      setWasTriggered(true)
    }
  }, [wasTriggered])

  if (!wasTriggered) {
    return (
      <>
        <GlobalCommands commands={{'item-pickers:open-projects': handleGlobalCommand}} />
        {anchorElement({
          onClick: () => {
            setWasTriggered(true)
          },
          onKeyPress: (event: React.KeyboardEvent<HTMLLIElement>) => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setWasTriggered(true)
            }
          },
        })}
      </>
    )
  }

  return (
    <ItemPickerFetcher
      triggerOpen
      anchorElement={props => anchorElement(props)}
      {...rest}
      pickerId="list-header-projects-picker"
    />
  )
}

function ItemPickerFetcher({repo, owner, ...rest}: ProjectPickerProps) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<ProjectPickerRelayQuery$data | null>(null)

  useEffect(() => {
    if (IS_SERVER) return

    clientSideRelayFetchQueryRetained<ProjectPickerRelayQuery>({
      environment,
      query: ProjectPickerGraphqlQuery,
      variables: {owner, repo},
    }).subscribe({
      next: internalData => {
        setData(internalData)
        setIsLoading(false)
        setIsError(false)
      },
      error: () => {
        setIsError(true)
      },
    })
  }, [environment, fetchKey, owner, repo])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit projects right now',
    anchorElement: rest.anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(fetchKey + 1))
  }

  return <SelectPanelWrapper projectsData={data} owner={owner} repo={repo} isLoading={isLoading} {...rest} />
}

function SelectPanelWrapper({
  pickerId,
  selectedProjects: initialSelected,
  projectsData: initialProjectsData,
  onSave,
  anchorElement,
  insidePortal,
  owner,
  repo,
  isLoading = false,
  triggerOpen = false,
  readonly,
  title,
  getSelectedProjects,
}: SelectPanelWrapperProps) {
  const [projectsData, setProjectsData] = useState<ProjectPickerRelayQuery$data | null>(initialProjectsData)
  const [inFlightSubscription, setInFlightSubscription] = useState<Subscription | null>(null)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const relayEnvironment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const [filter, setFilter] = useState('')

  const fetchSearchData = useCallback(
    (searchQuery: string) => {
      setSearchLoading(true)
      const subscription = fetchQuery<ProjectPickerRelayQuery>(relayEnvironment, ProjectPickerGraphqlQuery, {
        repo,
        owner,
        query: searchQuery,
      }).subscribe({
        next: (data: ProjectPickerRelayQuery$data) => {
          if (data !== null) {
            setProjectsData(data)
          }
          setSearchLoading(false)
        },
        error: () => {
          setSearchLoading(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchProjects,
          })
        },
      })
      setInFlightSubscription(subscription)
    },
    // Remove dependency on `addToast` to prevent re-renders and re-trigger the fetching
    // when an error is thrown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owner, relayEnvironment, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  useEffect(() => {
    if (filter.length === 0) {
      setProjectsData(initialProjectsData)
    } else {
      debounceFetchSearchData(filter)
    }
    inFlightSubscription?.unsubscribe()
    return () => {
      setSearchLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceFetchSearchData, filter, initialProjectsData])

  const recentProjects = useMemo(
    () =>
      (projectsData?.repository?.owner?.recentProjects?.edges || []).flatMap(a =>
        a?.node
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a.node)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  const repositoryProjects = useMemo(
    () =>
      (projectsData?.repository?.projectsV2?.nodes || []).flatMap(a =>
        a
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  const organizationProjects = useMemo(
    () =>
      (projectsData?.repository?.owner?.projectsV2?.edges || []).flatMap(a =>
        a?.node
          ? // eslint-disable-next-line no-restricted-syntax
            [readInlineData<ProjectPickerRelay$key>(ProjectPickerRelayFragment, a.node)].filter(
              item => item.closed === false,
            )
          : [],
      ),
    [projectsData],
  )

  //
  // Presentation logic
  //

  const initialSelectedProjects = useMemo(
    () =>
      getSelectedProjects
        ? getSelectedProjects([...recentProjects, ...repositoryProjects, ...organizationProjects])
        : initialSelected,
    [getSelectedProjects, initialSelected, organizationProjects, recentProjects, repositoryProjects],
  )

  // Array of Project IDs
  const [selected, setSelected] = useState<string[]>(() => initialSelectedProjects.map(p => p.id))
  // Reset the state for selected projects when the issueId changes
  useEffect(() => {
    setSelected(initialSelectedProjects.map(p => p.id))
  }, [initialSelectedProjects, pickerId])

  /// Convert a Project to an ItemInput object that SelectPanel can render
  const generateItemProps: {(groupId: string, p: ProjectPickerRelayProject): ItemInput} = useCallback(
    (groupId, p) => {
      const {title: text, id} = p

      // To prevent at-limit items being removed from the project when opening picker once the projects reached the limit
      const variant =
        p.hasReachedItemsLimit && !initialSelectedProjects.some(proj => proj.id === id)
          ? ('danger' as const)
          : ('default' as const)

      const descriptionVariant = p.hasReachedItemsLimit ? ('block' as const) : ('inline' as const)

      const item = {
        text,
        description: p.hasReachedItemsLimit ? `${LABELS.projectItemsLimitReached}` : undefined,
        descriptionVariant,
        id,
        groupId,
        selected: selected.includes(id),
        disabled: readonly || !p.viewerCanUpdate,
        leadingVisual: TableIcon,
        variant,
        sx: {wordBreak: 'break-word'},
      }

      return item
    },
    [initialSelectedProjects, selected, readonly],
  )

  /// Set of all projects in the picker
  const allProjects: Record<string, ProjectPickerRelayProject[]> = useMemo(() => {
    return {
      selected: initialSelectedProjects,
      recent: recentProjects.filter(
        p =>
          !initialSelectedProjects
            .map(function (pr) {
              return pr.id
            })
            .includes(p.id),
      ),
      repository: repositoryProjects.filter(
        p =>
          !initialSelectedProjects
            .map(function (pr) {
              return pr.id
            })
            .includes(p.id),
      ),
      organization: organizationProjects.filter(
        p =>
          !initialSelectedProjects
            .map(function (pr) {
              return pr.id
            })
            .includes(p.id),
      ),
    }
  }, [initialSelectedProjects, recentProjects, repositoryProjects, organizationProjects])

  const projectsToSelect = useMemo(
    () => Object.keys(allProjects).flatMap(pKey => allProjects[pKey]!.map(p => generateItemProps(pKey, p))),
    [allProjects, generateItemProps],
  )

  /// Subset of projects that have been selected
  const selectedProjects = useMemo(
    () => projectsToSelect.filter(p => p.id && selected.includes(p.id.toString())),
    [selected, projectsToSelect],
  )

  /// Optional search by filtering project title
  const filteredProjects = useMemo(() => {
    // We have nothing to filter
    if (projectsToSelect.length < 1) return []

    // We have nothing to filter by
    if (filter === '') {
      return projectsToSelect
    }

    const filtered = projectsToSelect.filter(
      item => (item.text || '').toLowerCase().indexOf(filter.trim().toLowerCase()) >= 0,
    )

    // We have no filter matches
    if (filtered.length < 1) return []
    return filtered
  }, [filter, projectsToSelect])

  /// Groups to render
  const groups = useMemo(
    () =>
      [
        initialSelectedProjects.length > 0 ? selectedGroup : null,
        recentProjects.length > 0 ? recentGroup : null,
        repositoryProjects.length > 0 ? repoGroup : null,
        organizationProjects.length > 0 ? orgGroup : null,
      ].filter(group => group !== null),
    [initialSelectedProjects.length, recentProjects.length, repositoryProjects.length, organizationProjects.length],
  )

  const handleSelectionChange = useCallback(
    (selection: ItemInput[]) => {
      // Find out if the selection now excludes a previously selected item, i.e. the diff
      const diff = selectedProjects.filter(p => !selection.includes(p)).map(p => p.id)
      let projectIds: Array<string | number> = selected

      // Handle selected projects
      const selectedIds = selection
        // Filter out items that don't have IDs
        .filter(item => item.id !== undefined)
        // Pull out the IDs and cast them
        .map(item => item.id) as Array<string | number>
      projectIds = [...projectIds, ...selectedIds]

      // Handle removed projects
      if (diff.length > 0) {
        projectIds = projectIds.filter(p => !diff.includes(p))
      }

      const projectIdsSet = new Set(projectIds.map(id => id.toString()))
      setSelected([...projectIdsSet])
    },
    [setSelected, selectedProjects, selected],
  )

  const {updateOpenState} = useItemPickersContext()
  const [isOpen, setOpen] = useState(triggerOpen)

  const onSpaceKeyPress = (event: KeyboardEvent) => {
    if (isOpen) {
      const activeOption = document.querySelector(SELECTORS.activePickerOption(IDS.itemPickerRootId))

      if (activeOption) {
        const activeDataId = activeOption.getAttribute('data-id')
        const item = [...filteredProjects.values()].find(i => i.id === activeDataId)

        if (item && item.id) {
          event.preventDefault()
          event.stopPropagation()

          setSelected(prevState => {
            const newState = [...prevState]
            const index = newState.indexOf(item.id!.toString())
            if (index > -1) {
              newState.splice(index, 1)
            } else {
              newState.push(item.id!.toString())
            }
            return newState
          })
        }
      }
    }
  }

  useKeyPress([' '], onSpaceKeyPress, {
    triggerWhenInputElementHasFocus: true,
    triggerWhenPortalIsActive: true,
  })

  const onCancel = useCallback(() => {
    setOpen(false)

    setFilter('')

    const selectionChanged =
      selected.length !== initialSelected.length ||
      selected.some(item => !initialSelected.some(item2 => item === item2.id))

    if (selectionChanged) {
      setSelected(initialSelected.map(p => (p.id ? p.id.toString() : '')))
    }
  }, [initialSelected, selected])

  const onOpenChange = useCallback(
    (open: boolean, gesture?: string) => {
      // Fix for an issue in safari where the issue would scroll down all the way
      // when the item picker is closed with escape
      if (!open && document.activeElement instanceof HTMLElement) {
        // eslint-disable-next-line github/no-blur
        document.activeElement?.blur()
      }
      if (gesture === 'cancel') {
        return
      }
      setOpen(open)
      updateOpenState(pickerId, open)
      if (!open) {
        setFilter('')

        // If any of the selected projects have the danger variant (meaning that the items limit is reached), open the dialog
        // We use danger and not hasReachedItemsLimit because the deprecated ItemInput type does not have hasReachedItemsLimit prop (node_modules/@primer/react/lib-esm/deprecated/ActionList/List.d.ts)
        const dangerItems = selectedProjects.filter(item => item.variant === 'danger')
        if (dangerItems.length > 0) {
          setIsDialogOpen(true)
        }

        // To prevent them from being checked on reopen of the picker (without reloading the page)
        const validSelected = selectedProjects
          .filter(item => item.variant !== 'danger')
          .map(p => p.id)
          .filter((id): id is string => id !== undefined)

        setSelected(validSelected)

        const uniqueProjects: ProjectPickerRelayProject[] = validSelected
          .flatMap(
            id =>
              Object.keys(allProjects)
                .flatMap(pKey => allProjects[pKey]!)
                .find(p => p.id === id) || [],
          )
          .filter((value, index, array) => {
            return array.findIndex(current => current.id === value.id) === index
          })
        onSave(uniqueProjects.filter(p => p !== undefined))
      }
    },
    [updateOpenState, pickerId, selectedProjects, onSave, allProjects],
  )

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [setIsDialogOpen])

  return (
    <ProjectPickerBase
      anchorElement={anchorElement}
      title={title}
      items={filteredProjects}
      selectedProjects={selectedProjects}
      groups={groups}
      onOpenChange={onOpenChange}
      isDialogOpen={isDialogOpen}
      handleDialogClose={handleDialogClose}
      insidePortal={insidePortal}
      onCancel={onCancel}
      onSelectionChange={handleSelectionChange}
      triggerOpen={isOpen}
      isLoading={isLoading || searchLoading}
      onFilterChange={setFilter}
      filterValue={filter}
    />
  )
}

try{ ProjectPicker.displayName ||= 'ProjectPicker' } catch {}
try{ ProjectPickerRelay.displayName ||= 'ProjectPickerRelay' } catch {}
try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ SelectPanelWrapper.displayName ||= 'SelectPanelWrapper' } catch {}